import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import '@/types/router' // Импортируем расширения типов

// Компоненты страниц
const HomePage = () => import('@/pages/HomePage.vue')
const ModelsPage = () => import('@/pages/ModelsPage.vue')
const ModelDetailPage = () => import('@/pages/ModelDetailPage.vue')
const ChatPage = () => import('@/pages/ChatPage.vue')
const RankingsPage = () => import('@/pages/RankingsPage.vue')
const DocsPage = () => import('@/pages/DocsPage.vue')
const SignInPage = () => import('@/pages/SignInPage.vue')
const NotFoundPage = () => import('@/pages/NotFoundPage.vue')

// Компоненты личного кабинета
const DashboardPage = () => import('@/pages/account/DashboardPage.vue')
const ApiKeysPage = () => import('@/pages/account/ApiKeysPage.vue')
const BillingPage = () => import('@/pages/account/BillingPage.vue')
const SettingsPage = () => import('@/pages/account/SettingsPage.vue')

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'home',
    component: HomePage,
    meta: {
      title: 'Tenzr - Единая точка доступа ко всем LLM',
      layout: 'DefaultLayout'
    }
  },
  {
    path: '/models',
    name: 'models',
    component: ModelsPage,
    meta: {
      title: 'Models - Tenzr',
      layout: 'DefaultLayout'
    }
  },
  {
    path: '/models/:id',
    name: 'model-detail',
    component: ModelDetailPage,
    meta: {
      title: 'Model Details - Tenzr',
      layout: 'DefaultLayout'
    }
  },
  {
    path: '/chat',
    name: 'chat',
    component: ChatPage,
    meta: {
      title: 'Chat - Tenzr',
      requiresAuth: true,
      layout: 'ChatLayout'
    }
  },
  {
    path: '/rankings',
    name: 'rankings',
    component: RankingsPage,
    meta: {
      title: 'Rankings - Tenzr',
      layout: 'DefaultLayout'
    }
  },
  {
    path: '/docs',
    name: 'docs',
    component: DocsPage,
    meta: {
      title: 'Documentation - Tenzr',
      layout: 'DocsLayout'
    }
  },
  {
    path: '/signin',
    name: 'signin',
    component: SignInPage,
    meta: {
      title: 'Sign In - Tenzr',
      layout: 'AuthLayout'
    }
  },
  {
    path: '/auth/callback',
    name: 'auth-callback',
    component: SignInPage,
    meta: {
      title: 'Завершение авторизации - Tenzr',
      layout: 'AuthLayout'
    }
  },
  // Account routes
  {
    path: '/account',
    redirect: '/account/dashboard'
  },
  {
    path: '/account/dashboard',
    name: 'dashboard',
    component: DashboardPage,
    meta: {
      title: 'Dashboard - Tenzr',
      requiresAuth: true,
      layout: 'AccountLayout'
    }
  },
  {
    path: '/account/api-keys',
    name: 'api-keys',
    component: ApiKeysPage,
    meta: {
      title: 'API Keys - Tenzr',
      requiresAuth: true,
      layout: 'AccountLayout'
    }
  },
  {
    path: '/account/billing',
    name: 'billing',
    component: BillingPage,
    meta: {
      title: 'Billing - Tenzr',
      requiresAuth: true,
      layout: 'AccountLayout'
    }
  },
  {
    path: '/account/settings',
    name: 'settings',
    component: SettingsPage,
    meta: {
      title: 'Settings - Tenzr',
      requiresAuth: true,
      layout: 'AccountLayout'
    }
  },
  // 404 страница (должна быть последней)
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: NotFoundPage,
    meta: {
      title: '404 - Страница не найдена - Tenzr',
      layout: 'DefaultLayout'
    }
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    return { top: 0 }
  }
})

// Utility function for loading auth store
const getAuthStore = async () => {
  const { useAuthStore } = await import('@/stores/auth')
  return useAuthStore()
}

// Global navigation guards
router.beforeEach(async (to, from, next) => {
  try {
    // Update document title
    if (to.meta?.title) {
      document.title = to.meta.title as string
    }
    
    // Handle auth callback routes (bypass other checks)
    if (to.name === 'auth-callback') {
      next()
      return
    }
    
    const authStore = await getAuthStore()
    
    // Check if route requires authentication
    if (to.meta?.requiresAuth) {
      if (!authStore.isAuthenticated) {
        console.log(`🔒 Route Guard: Redirecting to signin. Requested: ${to.fullPath}`)
        // Redirect to signin page with return URL
        next({
          name: 'signin',
          query: { redirect: to.fullPath }
        })
        return
      }
      
      // Проверка ролей, если они указаны
      if (to.meta.roles && to.meta.roles.length > 0) {
        const userRoles = authStore.user?.roles || []
        const hasRequiredRole = to.meta.roles.some(role => userRoles.includes(role))
        
        if (!hasRequiredRole) {
          console.warn(`🚫 Access denied: User doesn't have required roles`, {
            required: to.meta.roles,
            userRoles
          })
          next({ name: 'not-found' })
          return
        }
      }
      
      console.log(`✅ Route Guard: Access granted to ${to.fullPath}`)
    }
    
    // If authenticated user tries to access signin page, redirect to dashboard
    if (to.name === 'signin' || to.name === 'auth-callback') {
      if (authStore.isAuthenticated) {
        const redirectTo = to.query.redirect as string || '/account/dashboard'
        console.log(`🔄 Already authenticated, redirecting to: ${redirectTo}`)
        next(redirectTo)
        return
      }
    }
    
    next()
  } catch (error) {
    console.error('❌ Route Guard Error:', error)
    // В случае ошибки, разрешаем переход, чтобы не заблокировать навигацию
    next()
  }
})

// After each navigation
router.afterEach((to, from) => {
  // Log navigation for debugging (только в dev режиме)
  if (import.meta.env.DEV) {
    console.log(`📍 Navigation: ${from.fullPath} → ${to.fullPath}`)
  }
  
  // Scroll to top on route change (unless it's the same page with different hash)
  if (to.path !== from.path) {
    window.scrollTo(0, 0)
  }
})

export default router 